import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableCard from 'src/components/Table/TableCard';
import { DetailCard } from 'src/components/summary/DetailCard';
import { SummaryCardSkeleton } from 'src/components/summary/SummaryCardSkeleton';
import { SummaryGrid } from 'src/components/summary/SummaryGrid';
import { SummaryValue } from 'src/components/summary/SummaryValue';
import { useDevicePolicyDetails, useInfiniteDevicePolicyHistory } from 'src/hooks/wdacPolicy';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { formatDate } from 'src/utils/formatTime';

const compactCellSx = {
  fontSize: '0.7rem',
  py: 0.75,
  lineHeight: 1.3,
};

const compactHeaderSx = {
  fontSize: '0.7rem',
  fontWeight: 600,
  py: 0.75,
};

const formatYesNo = value => (value == null ? undefined : value ? 'Yes' : 'No');

const DevicePolicyDetailsModal = ({ open, onClose, deviceId, envPolicyVersionId, fallbackRow }) => {
  const navigate = useNavigate();
  const detailsQuery = useDevicePolicyDetails({ deviceId, envPolicyVersionId, enabled: open });
  const historyQuery = useInfiniteDevicePolicyHistory({
    deviceId,
    envPolicyVersionId,
    enabled: open,
  });
  const details = detailsQuery.data;
  const title = details?.policyName || fallbackRow?.policyName || 'Policy Details';
  const envPolicyId = fallbackRow?.id;

  const handleViewPolicy = useCallback(() => {
    onClose();
    navigate(`${PORTAL_ROUTE}/policy-details/${envPolicyId}/general`);
  }, [navigate, envPolicyId, onClose]);

  const history = useMemo(
    () => historyQuery.data?.pages?.flatMap(page => page.history || []) || [],
    [historyQuery.data],
  );

  const loadMoreHistory = useCallback(() => {
    if (historyQuery.hasNextPage) {
      historyQuery.fetchNextPage();
    }
  }, [historyQuery]);

  const [historySentryRef] = useInfiniteScroll({
    loading: historyQuery.isFetchingNextPage,
    hasNextPage: historyQuery.hasNextPage,
    disabled: !open,
    onLoadMore: loadMoreHistory,
    rootMargin: '0px 0px 240px 0px',
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#3f3f3f' } }}>
      <DialogTitle sx={{ pr: 5 }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="inherit" component="span">
            {title}
          </Typography>
          {envPolicyId && (
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              onClick={handleViewPolicy}
              sx={{ cursor: 'pointer', color: '#7DFF92' }}>
              <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 500 }}>
                View Policy
              </Typography>
              <Iconify icon="mdi:open-in-new" width={14} height={14} />
            </Stack>
          )}
        </Stack>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: '#CFCFCF' }}>
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {detailsQuery.isLoading ? (
            <SummaryCardSkeleton rows={2} />
          ) : (
            <DetailCard compact>
              <Stack spacing={1.25}>
                <SummaryValue compact label="Policy ID">
                  {details?.policyId}
                </SummaryValue>
                <SummaryGrid compact>
                  <SummaryValue compact label="Policy Version">
                    {details?.policyVersion || fallbackRow?.policyVersion}
                  </SummaryValue>
                  <SummaryValue compact label="Policy Type">
                    {details?.policyType || fallbackRow?.policyType}
                  </SummaryValue>
                  <SummaryValue compact label="Base Policy Mode">
                    {details?.basePolicyMode || fallbackRow?.basePolicyMode}
                  </SummaryValue>
                </SummaryGrid>
                <SummaryGrid compact>
                  <SummaryValue compact label="On System">
                    {formatYesNo(details?.isOnSystem)}
                  </SummaryValue>
                  <SummaryValue compact label="On Disk">
                    {formatYesNo(details?.isOnDisk)}
                  </SummaryValue>
                </SummaryGrid>
                <SummaryGrid compact>
                  <SummaryValue compact label="Last CI Tool Date">
                    {details?.lastCIToolDate ? formatDate(details.lastCIToolDate) : undefined}
                  </SummaryValue>
                  <SummaryValue compact label="Last 3099 Event Date">
                    {details?.last3099EventDate ? formatDate(details.last3099EventDate) : undefined}
                  </SummaryValue>
                </SummaryGrid>
              </Stack>
            </DetailCard>
          )}

          <TableCard title="History" maxHeight={320} edgePadding={3}>
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
                <TableRow>
                  <TableCell sx={{ ...compactHeaderSx, width: '20%' }}>History Date</TableCell>
                  <TableCell sx={{ ...compactHeaderSx, width: '15%' }}>On System</TableCell>
                  <TableCell sx={{ ...compactHeaderSx, width: '15%' }}>On Disk</TableCell>
                  <TableCell sx={{ ...compactHeaderSx, width: '25%' }}>Last CI Tool Date</TableCell>
                  <TableCell sx={{ ...compactHeaderSx, width: 'auto' }}>
                    Last 3099 Event Date
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
                {historyQuery.isLoading ? (
                  <LoadingRows columnCount={5} rowCount={4} />
                ) : (
                  <>
                    {history.map((entry, index) => (
                      <TableRow
                        key={entry.id || index}
                        ref={index === history.length - 1 ? historySentryRef : null}>
                        <TableCell sx={compactCellSx}>
                          {entry.historyDate ? formatDate(entry.historyDate) : '--'}
                        </TableCell>
                        <TableCell sx={compactCellSx}>
                          {formatYesNo(entry.isOnSystem) ?? '--'}
                        </TableCell>
                        <TableCell sx={compactCellSx}>
                          {formatYesNo(entry.isOnDisk) ?? '--'}
                        </TableCell>
                        <TableCell sx={compactCellSx}>
                          {entry.lastCIToolDate ? formatDate(entry.lastCIToolDate) : '--'}
                        </TableCell>
                        <TableCell sx={compactCellSx}>
                          {entry.last3099EventDate ? formatDate(entry.last3099EventDate) : '--'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!historyQuery.isLoading && historyQuery.isFetchingNextPage && (
                      <LoadingRows columnCount={5} rowCount={2} />
                    )}
                    {!historyQuery.isFetching && !history.length && (
                      <NoDataCell colSpan={5}>No history found.</NoDataCell>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableCard>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default DevicePolicyDetailsModal;
